
import { useState, useEffect, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as PlusSquareFill } from "bootstrap-icons/icons/plus-square-fill.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as CashStack } from "bootstrap-icons/icons/cash-stack.svg";
import { ReactComponent as CardList } from "bootstrap-icons/icons/card-list.svg";
import { ReactComponent as CardHeading } from "bootstrap-icons/icons/card-heading.svg";
import { ReactComponent as List } from "bootstrap-icons/icons/list.svg";
import Spinner from "../../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../../components/utils/CustomStyles";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import AxiosApiCall from "../../../api/AxiosApiCall";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import formatDate from "../../../components/utils/DateFormatter";


const IncentivesListPending = () => {
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const { permissions } = useContext(AuthContext);
  // console.log("all permission", permissions);
  const [updatingIncentive, setUpdatingIncentive] = useState(false);

  const navigate = useNavigate();
  const axios = AxiosApiCall();

  const fetchPendingIncentiveList = async () => {
    setLoading(true);
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) throw new Error("No access token found.");
      const response = await axios.get(ApiServices.pendingBranchOpeCreateIncentives, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const { status, message, data } = response.data;
      const pendingIncentives = data?.pending_requests || [];

      if (status === 200) {
        setIncentives(pendingIncentives.slice().reverse());
        toast.success(message, { toastId: "incentives-fetch-success" });
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingIncentiveList();
  }, []);

  const handleDeleteConfirmation = (incentiveId) => {
    setConfirmationLoading(true);
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      setConfirmationLoading(false);
      if (result.isConfirmed) {
        handleDeleteIncentive(incentiveId);
      }
    });
  };

  const handleDeleteIncentive = async (incentiveId) => {
    const formData = { incentiveId };
    try {
      setLoading(true);
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) throw new Error("No access token found.");
      const response = await axios.delete(ApiServices.deleteBranchOpeCreateIncentives, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: formData,
        withCredentials: true,
      });

      const { status, message } = response.data;
      if (status === 200) {
        toast.success(message, { toastId: "incentive-delete-success" });
        await fetchPendingIncentiveList();
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateIncentive = async (incentive) => {
    const { id, amount, description } = incentive;

    const { value: formValues } = await Swal.fire({
      title: "Update Branch Incentive",
      html: `
        <input id="amount" type="number" placeholder="Amount"
          class="w-full px-4 py-2 border border-gray-300 rounded-md mb-3"
          value="${amount.replace(/,/g, '')}" />
        <input id="description" type="text" placeholder="Description"
          class="w-full px-4 py-2 border border-gray-300 rounded-md mb-3"
          value="${description}" />
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Update Incentive",
      preConfirm: () => {
        const amount = parseFloat(document.getElementById("amount").value);
        const description = document.getElementById("description").value;
        if (!amount || !description) {
          Swal.showValidationMessage(`Amount and description are required`);
          return;
        }
        return { amount, description };
      },
    });

    if (formValues) {
      try {
        setUpdatingIncentive(true);
        const authData = JSON.parse(localStorage.getItem("auth"));
        const token = authData?.token;
        if (!token) throw new Error("No access token found.");
        const formData = {
          incentive_id: id,
          amount: formValues.amount,
          description: formValues.description,
        };

        const response = await axios.put(ApiServices.updateBranchOpeCreateIncentives, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        const { status, message } = response.data;
        if (status === 200) {
          toast.success(message, { toastId: "incentive-update-success" });
          await fetchPendingIncentiveList();
        } else {
          handleError({ response: { status, data: { message } } }, navigate);
        }
      } catch (error) {
        handleError(error, navigate);
      } finally {
        setUpdatingIncentive(false);
      }
    }
  };

  const processApprovalOrRejection = async (formData) => {
    try {
      setUpdatingIncentive(true);
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) throw new Error("No access token found.");

      // Prepare the payload according to your structure
      const payload = {
        incentive_id: formData.incentive_id,
        action: formData.action,
      };

      // Add conditional fields based on action
      if (formData.action === "approve") {
        payload.approved_amount = formData.approved_amount;
        payload.admin_notes = formData.admin_notes;
      } else if (formData.action === "reject") {
        payload.rejection_reason = formData.rejection_reason;
      }

      // console.log("form data fro appr or rejec", formData)
      const response = await axios.put(ApiServices.processBranchOpeCreateIncentives, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const { status, message } = response.data;
      if (status === 200) {
        toast.success(message, { toastId: "incentive-approv-success" });
        await fetchPendingIncentiveList();
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setUpdatingIncentive(false);
    }
  };

  const handleApprovalConfirmation = async (incentive) => {
    const { id, amount, description, rejection_reason, approved_amount, admin_notes } = incentive;

    const { value: action } = await Swal.fire({
      title: "Choose Action",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Approve",
      denyButtonText: "Reject",
      cancelButtonText: "Cancel",
      icon: "question",
    });

    // Return if user cancels
    if (action === undefined) return;

    if (action === true) {
      // Approval flow
      const { value: approveValues } = await Swal.fire({
        title: "Approve Incentive",
        html: `
          <div class="text-left">
            <label class="block text-gray-700 mb-1">Approved Amount</label>
            <input id="approved_amount" type="number" placeholder="Amount"
              class="w-full px-4 py-2 border border-gray-300 rounded-md mb-3"
              value="${amount.replace(/,/g, '')}" />
            
            <label class="block text-gray-700 mb-1">Admin Notes</label>
            <textarea id="admin_notes" placeholder="Notes"
              class="w-full px-4 py-2 border border-gray-300 rounded-md mb-3"
              rows="3"></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Confirm Approval",
        preConfirm: () => {
          const approved_amount = parseFloat(document.getElementById("approved_amount").value);
          const admin_notes = document.getElementById("admin_notes").value;

          if (!approved_amount || isNaN(approved_amount)) {
            Swal.showValidationMessage("Please enter a valid amount");
            return;
          }
          if (!admin_notes) {
            Swal.showValidationMessage("Admin notes are required");
            return;
          }
          return { approved_amount, admin_notes };
        },
      });

      if (approveValues) {
        await processApprovalOrRejection({
          incentive_id: id,
          action: "approve",
          approved_amount: approveValues.approved_amount,
          admin_notes: approveValues.admin_notes,
        });
      }
    } else if (action === false) {
      // Rejection flow
      const { value: rejectionReason } = await Swal.fire({
        title: "Reject Incentive",
        html: `
          <div class="text-left">
            <label class="block text-gray-700 mb-1">Rejection Reason</label>
            <textarea id="rejection_reason" placeholder="Reason for rejection"
              class="w-full px-4 py-2 border border-gray-300 rounded-md mb-3"
              rows="3"></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Confirm Rejection",
        preConfirm: () => {
          const rejection_reason = document.getElementById("rejection_reason").value;
          if (!rejection_reason) {
            Swal.showValidationMessage("Rejection reason is required");
            return;
          }
          return rejection_reason;
        },
      });

      if (rejectionReason) {
        await processApprovalOrRejection({
          incentive_id: id,
          action: "reject",
          rejection_reason: rejectionReason,
        });
      }
    }
  };


  const ExpandableComponent = ({ data }) => (
    <div className="flex justify-start p-4 relative">
      {permissions.some(permission => permission.name === 'admin.edit') && (
        <div className="mx-2 my-2 group relative">
          <button onClick={() => handleUpdateIncentive(data)} disabled={updatingIncentive}>
            <PencilSquare className="w-5 h-5 text-dash4" />
          </button>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-dash4 text-xs px-2 py-1 rounded">Update</div>
        </div>
      )}
      {permissions.some(permission => permission.name === 'admin.create') && (
        <>
          <div className="mx-2 my-2 group relative">
            <button onClick={() => handleDeleteConfirmation(data.id)} disabled={confirmationLoading}>
              <Trash className="w-5 h-5 text-dash3" />
            </button>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-dash3 text-xs px-2 py-1 rounded">Delete</div>
          </div>
          <div className="mx-2 my-2 group relative">
            <button onClick={() => handleApprovalConfirmation(data)} disabled={confirmationLoading}>
              <PlusSquareFill className="w-5 h-5 text-darkGreen3" />
            </button>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-dash4 text-xs px-2 py-1 rounded">Approval</div>
          </div>
        </>
      )}
    </div>
  );

  const handleFilter = (e) => {
    setFilterText(e.target.value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const columns = [
    {
      name: "Reference",
      selector: (row) => row.reference || "NOT AVAILABLE",
      sortable: true,
      width: "250px",
    },
    {
      name: "Amount",
      selector: (row) => row.amount || "NOT AVAILABLE",
      sortable: true,
      width: "150px",
    },
    {
      name: "Description",
      selector: (row) => row.description || "NOT AVAILABLE",
      sortable: true,
      width: "200px",
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={row.status === "pending" ? "text-orange1" : "text-dash3"}>
          {row.status}
        </span>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: "Date Created",
      selector: (row) => row.created_at ? formatDate(row.created_at) : "NOT AVAILABLE",
      sortable: true,
      width: "200px",
    },
  ];

  const filteredItems = incentives.filter((item) =>
    Object.keys(item).some((key) =>
      item[key]?.toString().toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const data = filteredItems.map((incentives) => ({
    reference: incentives.reference || "NOT AVAILABLE",
    amount: incentives.amount || "NOT AVAILABLE",
    description: incentives.description,
    status: incentives.status,
    created_at: incentives.created_at,
  }));

  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  return (
    <MainLayouts>
      <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
        <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
          <nav className="bg-gray-200 py-0 px-4 rounded-lg">
            <ol className="list-none p-0 inline-flex mb-2">
              <li className="flex items-center">
                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Operations</p>
                <ChevronRightIcon />
              </li>
              <li className="flex items-center">
                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Incentives</p>
                <ChevronRightIcon />
              </li>
              <li className="flex items-center">
                <p className="text-orange1">Pending</p>
              </li>
            </ol>
          </nav>
        </section>
        <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between">
          <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
            <input
              type="search"
              className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
              placeholder="Search"
              value={filterText}
              onChange={handleFilter}
            />
          </div>
        </div>
        {loading ? (
          <Spinner />
        ) : (
          <DataTable
            columns={columns}
            data={sortedData}
            pagination
            highlightOnHover
            striped
            customStyles={CustomStyles}
            noDataComponent={<div>No Pending incentives found</div>}
            expandableRows

            expandableRowsComponent={(props) => (
              <ExpandableComponent data={props.data} />
            )}
          />
        )}
      </div>
    </MainLayouts>
  );
};

export default IncentivesListPending;







